import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
  Chip,
  Box,
  Button,
  TextField,
  Typography
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';

// Base URL for API (for debugging)
const API_BASE_URL = process.env.NODE_ENV === 'development' 
  ? 'http://localhost:4000' 
  : '';

export const KinkSelector = ({ open, onClose, onSelect }) => {
  const [kinks, setKinks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    instruction: ''
  });

  const fetchKinks = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/kinks`);
      const data = await response.json();
      setKinks(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Error fetching kinks:', error);
      setKinks([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open) {
      fetchKinks();
    }
  }, [open]);

  const handleCreateSubmit = async () => {
    try {
      if (!formData.name) {
        throw new Error('Name is required');
      }

      const response = await fetch(`${API_BASE_URL}/api/kinks`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData)
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to save kink');
      }

      const newKink = await response.json();
      setCreateDialogOpen(false);
      setFormData({ name: '', instruction: '' });
      await fetchKinks();
      
      onSelect({ 
        id: newKink._id, 
        name: newKink.name,
        instruction: newKink.instruction
      });
    } catch (error) {
      console.error('Error saving kink:', error);
    }
  };

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            Select Kink
            <Button
              variant="contained"
              color="primary"
              size="small"
              onClick={() => setCreateDialogOpen(true)}
              startIcon={<AddIcon />}
            >
              Add Kink
            </Button>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ padding: 1 }}>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
              <CircularProgress />
            </Box>
          ) : (
            <TableContainer component={Paper}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ padding: 1 }}>Name</TableCell>
                    <TableCell sx={{ padding: 1 }}>Instruction</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {kinks.map((kink) => (
                    <TableRow 
                      key={kink._id}
                      sx={{ 
                        '&:hover': { bgcolor: 'action.hover' },
                        cursor: 'pointer'
                      }}
                      onClick={() => onSelect({ 
                        id: kink._id, 
                        name: kink.name,
                        instruction: kink.instruction
                      })}
                    >
                      <TableCell sx={{ padding: 1 }}>
                        <Chip color="primary" label={kink.name} />
                      </TableCell>
                      <TableCell sx={{ padding: 1 }}>
                        <Typography variant="body2" sx={{ 
                          maxWidth: '400px',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap'
                        }}>
                          {kink.instruction}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={createDialogOpen} onClose={() => setCreateDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Create kink</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <TextField
              label="Name"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              fullWidth
              required
              sx={{ mb: 2 }}
            />
            <TextField
              label="Instruction"
              value={formData.instruction}
              onChange={(e) => setFormData({ ...formData, instruction: e.target.value })}
              fullWidth
              multiline
              rows={4}
              required
              sx={{ mb: 2 }}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleCreateSubmit} variant="contained">
            Create
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

